import { Component } from '@angular/core';
import { finalize } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { RRE } from '../../models/rre-management.model';
import { RreManagementService } from '../../services/rre-management.service';
import { BaseTableComponent } from '../../shared/base/base-table.component';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';
import { resolveLastUpdatedBy } from '../../shared/utils/user.utils';
import { RreManagementModalComponent } from './rre-management-modal/rre-management-modal.component';

@Component({
  selector: 'app-rremanagement',
  standalone: false,
  templateUrl: './rremanagement.component.html',
  styleUrl: './rremanagement.component.css',
})
export class RreManagementComponent extends BaseTableComponent<RRE> {
  readonly displayedColumns: string[] = [
    'rre',
    'rreName',
    'reportingGroup',
    'queryReportingOn',
    'claimReportingOn',
    'isocpAccountDesigneeOnly',
    'comment',
    'lastUpdated',
    'lastUpdatedBy',
    'actions',
  ];

  readonly pageSizes = [10, 20, 50];
  override sortBy = 'rre';
  override sortDir: 'asc' | 'desc' = 'asc';

  constructor(
    private rreManagementService: RreManagementService,
    private dialog: MatDialog
  ) {
    super();
  }

  protected override loadData(skip: number, count: number): void {
    this.loading = true;
    this.errorMessage = '';

    this.rreManagementService
      .getRREs(this.sortBy, this.sortDir, this.searchTerm, skip, count)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (result) => {
          this.dataSource.data = result.rres ?? [];
          this.totalCount = result.totalCount ?? this.dataSource.data.length;
        },
        error: (error) => {
          this.errorMessage = formatHttpErrorMessage('Failed to load RRE records', error);
          this.dataSource.data = [];
          this.totalCount = 0;
        },
      });
  }

  addRRE(): void {
    const dialogRef = this.dialog.open(RreManagementModalComponent, {
      width: '560px',
      maxWidth: '95vw',
      disableClose: true,
      restoreFocus: false,
      data: { isEdit: false },
    });

    dialogRef.afterClosed().subscribe((payload?: RRE) => {
      if (!payload) return;

      const requestPayload: RRE = {
        ...payload,
        LastUpdatedBy: resolveLastUpdatedBy(),
      };

      this.loading = true;
      this.errorMessage = '';
      this.rreManagementService
        .addRRE(requestPayload)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: () => this.loadDataWrapper(),
          error: (error) => {
            this.errorMessage = formatHttpErrorMessage('Failed to add RRE record', error);
          },
        });
    });
  }

  onEdit(row: RRE): void {
    const dialogRef = this.dialog.open(RreManagementModalComponent, {
      width: '560px',
      maxWidth: '95vw',
      disableClose: true,
      restoreFocus: false,
      data: { isEdit: true, record: row },
    });

    dialogRef.afterClosed().subscribe((payload?: RRE) => {
      if (!payload) return;

      const requestPayload: RRE = {
        ...payload,
        LastUpdatedBy: resolveLastUpdatedBy(),
      };

      this.loading = true;
      this.errorMessage = '';
      this.rreManagementService
        .updateRRE(requestPayload)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: () => this.loadDataWrapper(),
          error: (error) => {
            this.errorMessage = formatHttpErrorMessage('Failed to update RRE record', error);
          },
        });
    });
  }
}
